import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Upload, FileArchive } from 'lucide-react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Field } from './Dialog';
import { studentPortal } from '../services/api';

const ACTIVE = ['receiving', 'validating', 'transferring', 'ready', 'committing'];
const labels = { receiving: 'Uploading files', validating: 'Validating files', transferring: 'Preparing your project',
  ready: 'Ready to import', committing: 'Finalizing import', completed: 'Project imported', cancelled: 'Import cancelled', failed: 'Import failed' };
const sizeLabel = value => value < 1024 * 1024 ? `${Math.ceil(value / 1024)} KiB` : `${(value / 1024 / 1024).toFixed(1)} MiB`;
const errorMessage = error => error.response?.data?.error || error.message || 'Unable to complete the import.';
const requestId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(18)), byte => byte.toString(16).padStart(2, '0')).join('');

export default function WorkspaceImportDialog({ labId, allowUpload, ready, onClose, onOpenProject }) {
  const client = useQueryClient();
  const [selection, setSelection] = useState([]);
  const [sourceType, setSourceType] = useState('files');
  const [sourceName, setSourceName] = useState('');
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [opening, setOpening] = useState(false);
  const filesInput = useRef(null), folderInput = useRef(null), zipInput = useRef(null);
  const uploadController = useRef(null);
  const history = useQuery({ queryKey: ['workspaceImports', labId], queryFn: () => studentPortal.listImports(labId) });
  const detail = useQuery({ queryKey: ['workspaceImport', labId, jobId], enabled: !!jobId,
    queryFn: () => studentPortal.getImport(labId, jobId),
    refetchInterval: query => ['receiving', 'validating', 'transferring', 'committing'].includes(query.state.data?.import?.status) ? 2000 : false });
  const job = detail.data?.import;
  const limits = history.data?.limits || { files: 2000, uploadBytes: 50 * 1024 * 1024, unpackedBytes: 200 * 1024 * 1024 };

  useEffect(() => {
    if (!jobId) {
      const active = history.data?.imports?.find(item => ACTIVE.includes(item.status));
      if (active) setJobId(active.id);
    }
  }, [jobId, history.data]);

  // The shared Dialog keeps focus inside and hands it back on close; leaving
  // the dialog also abandons an upload still in flight.
  useEffect(() => () => uploadController.current?.abort(), []);

  const remember = result => {
    client.setQueryData(['workspaceImport', labId, result.import.id], result);
    client.invalidateQueries({ queryKey: ['workspaceImports', labId] });
    client.invalidateQueries({ queryKey: ['studentLab', labId] });
    setJobId(result.import.id);
  };

  const upload = useMutation({
    mutationFn: async () => {
      setError(null);
      const created = await studentPortal.createImport(labId, { sourceType, sourceName, requestId: requestId() });
      remember(created);
      const form = new FormData();
      form.append('paths', JSON.stringify(selection.map(file => file.webkitRelativePath || file.name)));
      selection.forEach(file => form.append('files', file, file.name));
      uploadController.current = new AbortController();
      return studentPortal.uploadImport(labId, created.import.id, form, {
        signal: uploadController.current.signal,
        onUploadProgress: event => setProgress(event.total ? Math.round(event.loaded / event.total * 100) : 0)
      });
    },
    onSuccess: result => { remember(result); setSelection([]); },
    onError: err => { if (err.code !== 'ERR_CANCELED') setError(errorMessage(err)); }
  });
  const commit = useMutation({ mutationFn: () => studentPortal.commitImport(labId, jobId),
    onSuccess: remember, onError: err => setError(errorMessage(err)) });
  const cancel = useMutation({ mutationFn: async () => {
    uploadController.current?.abort();
    return studentPortal.cancelImport(labId, jobId);
  }, onSuccess: remember, onError: err => setError(errorMessage(err)) });

  const busy = upload.isPending || commit.isPending || cancel.isPending || opening;
  const active = jobId && (!job || ACTIVE.includes(job.status));
  const select = (event, type) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = '';
    if (!chosen.length) return;
    setError(null);
    if (chosen.length > limits.files || chosen.reduce((sum, file) => sum + file.size, 0) > limits.uploadBytes) {
      setError(`Choose at most ${limits.files} files totaling ${sizeLabel(limits.uploadBytes)}.`); return;
    }
    setSelection(chosen); setSourceType(type); setJobId(null);
    setSourceName(type === 'folder' ? chosen[0].webkitRelativePath.split('/')[0]
      : type === 'zip' ? chosen[0].name.replace(/\.zip$/i, '') : 'My project');
    setProgress(0);
  };
  const openProject = async id => {
    setOpening(true); setError(null);
    try { await onOpenProject(id); }
    catch (err) { setError(errorMessage(err)); }
    finally { setOpening(false); }
  };
  const preview = job?.files?.length ? job.files : selection.map(file => ({ path: file.webkitRelativePath || file.name, size: file.size }));

  return (
    <Dialog
      size="lg"
      onClose={onClose}
      labelledBy="workspace-import-title"
      isDirty={selection.length > 0 || upload.isPending}
      discardPrompt={upload.isPending
        ? { title: 'Stop the upload?', body: 'Your files are still uploading. Closing now cancels this import.', keep: 'Keep uploading', discard: 'Stop upload' }
        : { title: 'Discard the files you chose?', body: 'They have not been uploaded yet.', keep: 'Keep them', discard: 'Discard' }}
    >
      <DialogHeader id="workspace-import-title" title="Import existing code" icon={<FolderOpen size={20} />} onClose={onClose} />
      <DialogBody>
        <div>
          <p style={{ marginBottom: 16 }}>Upload a copy of your local project, review its files, then open it in your lab IDE. Each import gets a separate project folder.</p>
          {!allowUpload && <p role="status">Your instructor has disabled new imports. Previously imported projects can still be opened.</p>}
          {allowUpload && !ready && <p role="status">Wait for the environment and starter code to finish starting.</p>}
          {allowUpload && <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className="btn btn-secondary" disabled={busy || active || !ready} onClick={() => folderInput.current.click()}><FolderOpen size={16} /> Upload folder</button>
              <button className="btn btn-secondary" disabled={busy || active || !ready} onClick={() => filesInput.current.click()}><Upload size={16} /> Upload files</button>
              <button className="btn btn-secondary" disabled={busy || active || !ready} onClick={() => zipInput.current.click()}><FileArchive size={16} /> Upload ZIP</button>
            </div>
            <input ref={folderInput} type="file" aria-label="Choose project folder" webkitdirectory="" multiple hidden onChange={e => select(e, 'folder')} />
            <input ref={filesInput} type="file" aria-label="Choose project files" multiple hidden onChange={e => select(e, 'files')} />
            <input ref={zipInput} type="file" aria-label="Choose project ZIP" accept=".zip" hidden onChange={e => select(e, 'zip')} />
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Up to {limits.files} files, {sizeLabel(limits.uploadBytes)} uploaded, and {sizeLabel(limits.unpackedBytes)} unpacked. Use ZIP if folder selection is unavailable.
            </p>
          </>}
          {!!selection.length && !active && <Field label="Project name" htmlFor="import-project-name">
            <input id="import-project-name" className="form-input" value={sourceName} maxLength={128} disabled={busy} onChange={e => setSourceName(e.target.value)} />
          </Field>}
          {job && <div role="status" aria-live="polite" style={{ margin: '16px 0' }}>
            <strong>{labels[job.status] || job.status}</strong>
            {job.status === 'receiving' && upload.isPending && <div><progress max="100" value={progress} aria-label="Upload progress" /> {progress}%</div>}
            {job.destination && <p style={{ overflowWrap: 'anywhere', fontSize: 13 }}>Project folder: <code>{job.destination}</code></p>}
            {job.fileCount > 0 && <p>{job.fileCount} files · {sizeLabel(job.totalBytes)}</p>}
            {job.skippedMetadataFiles > 0 && <p>{job.skippedMetadataFiles} old import receipt(s) excluded from the project.</p>}
            {job.status === 'ready' && <p>Review the files below, then select Import project to add them to your workspace.</p>}
          </div>}
          {!!preview.length && <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 16, border: '1px solid var(--border-primary)', borderRadius: 8 }} tabIndex="0" aria-label="Files to import">
            <ul style={{ listStyle: 'none', padding: 12, margin: 0 }}>
              {preview.map(file => <li key={file.path} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13, padding: '3px 0' }}>
                <span style={{ overflowWrap: 'anywhere' }}>{file.path}</span><span style={{ whiteSpace: 'nowrap' }}>{sizeLabel(file.size)}</span>
              </li>)}
            </ul>
          </div>}
          {(error || job?.error || detail.error || history.error) && <p role="alert" style={{ color: 'var(--accent-danger)', margin: '12px 0' }}>
            {error || job?.error || errorMessage(detail.error || history.error)}
          </p>}
          {history.data?.imports?.some(item => item.status === 'completed') && <section aria-label="Previously imported projects" style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Imported projects</h3>
            {history.data.imports.filter(item => item.status === 'completed' && item.id !== job?.id).map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '8px 0' }}>
                <span>{item.sourceName} <small className="text-muted">({item.fileCount} files)</small></span>
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => openProject(item.id)} aria-label={`Open ${item.sourceName}`}>Open project</button>
              </div>
            ))}
          </section>}
        </div>
      </DialogBody>
      <DialogFooter>
        {active && job?.status !== 'committing' && <button className="btn btn-secondary" disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel import</button>}
        {!active && !!selection.length && <button className="btn btn-primary" disabled={busy || !allowUpload || !ready || !sourceName.trim()} onClick={() => upload.mutate()}>Upload and preview</button>}
        {job?.status === 'ready' && <button className="btn btn-primary" disabled={busy || !allowUpload || !ready} onClick={() => commit.mutate()}>Import project</button>}
        {job?.status === 'completed' && <button className="btn btn-primary" disabled={busy} onClick={() => openProject(job.id)}>Open imported project</button>}
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </DialogFooter>
    </Dialog>
  );
}
